<template>
  <ModalStep
    id="mentee-report-modal-content"
    data-test-id="mentee-report-session-modal-content"
    :header="{
      title: t('session.menteeReportModal.title'),
    }"
  >
    <template #bodyContent>
      <div class="c-info-wrapper">
        <p class="c-info-label" style="text-align: center">
          <strong v-html="t('session.menteeReportModal.body')"></strong>
        </p>
      </div>
      <CustomTextarea
        :label="`${t('session.menteeReportModal.input.report.label')}`"
        id="mentee-report-session"
        data-test-id="mentee-report-textarea"
        :placeholder="currentUserReportRef?.comment ? currentUserReportRef.comment : reportTemplateRef"
        :value="reportRef"
        @emit:change="onChange"
      />
    </template>

    <template #footer>
      <div class="c-btns-container">
        <CustomButton
          usage="button"
          type="submit"
          color="white"
          :text="t('ui.button.fillLater')"
          @emit:click="onCloseAndFillLater"
        />
        <CustomButton
          :is-disabled="!reportRef"
          :is-loading="isAddSessionMenteeReportPendingRef"
          usage="button"
          type="submit"
          color="primary"
          data-test-id="mentee-report-submit-button"
          :text="`${t('ui.button.continue')}`"
          icon-position="right"
          @emit:click="onSubmit"
        >
          <template #icon>
            <ArrowIcon color="white" />
          </template>
        </CustomButton>
      </div>
    </template>
  </ModalStep>
</template>

<script setup lang="ts">
import { useMutation } from '@tanstack/vue-query'
import { computed, ref, watch } from 'vue'
import { toast } from 'vue-sonner'

import useAccount from '@/features/auth/hooks/use-account.hook'
import useProgram from '@/features/programs/hooks/use-program.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import { V1AddSessionMenteeReportMutation } from '@/features/sessions/api'
import { useSessionInReview } from '@/features/sessions/hooks/use-in-review-session.hook'
import useSession from '@/features/sessions/hooks/use-session.hook'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import CustomTextarea from '@/features/ui/components/custom-textarea/custom-textarea.vue'
import ArrowIcon from '@/features/ui/components/icons/arrow-icon.vue'
import ModalStep from '@/features/ui/components/modal/modal-step/modal-step.vue'
import { selectGetReviewSessionModal } from '@/features/ui/store/modal/modal.selectors'
import modalsStore from '@/features/ui/store/modal/modal.store'
import { useI18n } from '@/lib/i18n'
import { invalidateSession, invalidateSessions } from '@/utils/lib/vue-query'

const reportRef = ref<string | undefined>(undefined)
const { t } = useI18n()

const { accountRef } = useAccount()

const reviewSessionModalRef = selectGetReviewSessionModal(modalsStore)
const selectedSessionInReviewIdRef = computed(() => reviewSessionModalRef.value?.attachedData.sessionId ?? null)
const { sessionRef } = useSession(selectedSessionInReviewIdRef)

const programIdRef = selectGetSelectedProgramId(programsStore)
const { programRef } = useProgram(programIdRef)

const { onClickNextStep, onCloseAndFillLater } = useSessionInReview()

// Get current user's report
const currentUserReportRef = computed(() => {
  return sessionRef.value?.menteeReports?.find((report) => report.userId === accountRef.value?.id)
})

// the objective linked to the session has its own template, the program one is the fallback
const reportTemplateRef = computed(
  () => sessionRef.value?.objective?.menteeReportTemplate || programRef.value?.settings.menteeReportTemplate || '',
)

// set base value for the report textarea
watch(
  [() => currentUserReportRef.value?.comment, reportTemplateRef],
  () => {
    if (currentUserReportRef.value?.comment || currentUserReportRef.value?.comment === '') {
      reportRef.value = currentUserReportRef.value?.comment
    } else {
      reportRef.value = reportTemplateRef.value
    }
  },
  { immediate: true },
)

function onChange(text: string) {
  reportRef.value = text
}

const { mutate: addSessionMenteeReport, isPending: isAddSessionMenteeReportPendingRef } = useMutation({
  mutationFn: V1AddSessionMenteeReportMutation,
  onError: () => {
    toast?.error(t('error.common'))
  },
  onSuccess: async () => {
    // on success, we invalidate the modal to pass the session to the COMPLETE status
    await invalidateSessions()
    await invalidateSession(selectedSessionInReviewIdRef.value!)

    toast?.success(t('session.menteeReportModal.success'))
    onClickNextStep()
  },
})

function onSubmit() {
  try {
    if (!selectedSessionInReviewIdRef.value) {
      throw new Error('No session id found')
    }

    if (!reportRef.value) {
      throw new Error('No report found')
    }
    addSessionMenteeReport({
      sessionId: selectedSessionInReviewIdRef.value,
      comment: reportRef.value,
    })
  } catch (error) {
    console.error(error)
    toast?.error(t('error.common'))
  }
}
</script>
